<section class="tc" data-testid="testrun-comments-panel" [attr.aria-labelledby]="'testrun-comments-heading'">
  <to-section-title
    #heading
    tabindex="-1"
    data-testid="testrun-comments-heading"
    headingId="testrun-comments-heading"
    [title]="'testRun.comments.title' | translate"
    [subtitle]="(canComment() ? 'testRun.comments.subtitle' : 'testRun.comments.subtitleReadOnly') | translate"
  >
    <button
      right
      type="button"
      class="tc__icon-btn"
      data-testid="testrun-comments-refresh"
      [attr.aria-label]="'testRun.comments.refreshAria' | translate"
      [disabled]="loading()"
      (click)="load()"
    >
      <mat-icon aria-hidden="true">refresh</mat-icon>
    </button>
  </to-section-title>

  @if (errorKey(); as key) {
    <p class="tc__error" role="alert" data-testid="testrun-comments-error">{{ key | translate }}</p>
  }

  @if (loading()) {
    <div class="tc__loading">
      <mat-spinner diameter="24" [attr.aria-label]="'testRun.comments.loading' | translate" />
    </div>
  } @else if (empty()) {
    <p class="tc__empty" data-testid="testrun-comments-empty">{{ 'testRun.comments.empty' | translate }}</p>
  } @else {
    <ol class="tc__list">
      @for (row of rows(); track row.comment.id) {
        @let comment = row.comment;
        @let authorName = comment.authorName ?? ('testRun.comments.handoverUnknownRecipient' | translate);
        <li
          class="tc__item"
          [class.tc__item--handover]="isHandover(comment)"
          [class.tc__item--previous]="row.previousRound"
          data-testid="testrun-comment-item"
        >
          <app-avatar [name]="authorName" [size]="28" [benutzerId]="comment.authorUserId" />

          <div class="tc__body">
            <div class="tc__meta">
              <span class="tc__author">{{ authorName }}</span>

              @if (row.previousRound) {
                <span class="tc__round" data-testid="testrun-comment-previous-round">
                  <mat-icon class="tc__round-icon" aria-hidden="true">history</mat-icon>
                  @if (row.roundNr !== null) {
                    {{ 'testRun.comments.fromRound' | translate: { nr: row.roundNr } }}
                  } @else {
                    {{ 'testRun.comments.fromEarlierRound' | translate }}
                  }
                </span>
              }

              @if (isHandover(comment)) {
                <span class="tc__handover-badge" data-testid="testrun-comment-handover-badge">
                  {{ 'testRun.comments.handoverBadge' | translate }}
                </span>
              } @else if (comment.stepResultId) {
                <span class="tc__step" data-testid="testrun-comment-step">
                  @if (row.reference; as reference) {
                    @if (reference.elementKey) {
                      <code class="tc__step-key" data-testid="testrun-comment-step-key">
                        {{ reference.elementKey }}
                      </code>
                      <span class="tc__step-sep" aria-hidden="true" data-testid="testrun-comment-step-separator">
                        ·
                      </span>
                    }
                    @if (reference.elementTitle) {
                      <span
                        class="tc__step-title"
                        data-testid="testrun-comment-step-title"
                        [title]="reference.elementTitle"
                      >
                        {{ reference.elementTitle }}
                      </span>
                      <span class="tc__step-sep" aria-hidden="true" data-testid="testrun-comment-step-separator">
                        ·
                      </span>
                    }
                    <span data-testid="testrun-comment-step-number">
                      {{ 'testRun.comments.stepNumber' | translate: { number: reference.stepNumber } }}
                    </span>
                  } @else {
                    <span class="tc__step-hint" data-testid="testrun-comment-step-hint">
                      <mat-icon class="tc__step-icon" aria-hidden="true">list_alt</mat-icon>
                      {{ 'testRun.comments.stepReference' | translate }}
                    </span>
                  }
                </span>

                @if (row.reference; as reference) {
                  <app-res-pill [status]="reference.status" [label]="reference.statusLabelKey | translate" />
                }
              }

              <time class="tc__when" [attr.datetime]="comment.createdAt">{{ comment.createdAt | date: 'short' }}</time>

              @if (isEdited(comment)) {
                <span class="tc__edited" data-testid="testrun-comment-edited">
                  {{ 'testRun.comments.edited' | translate }}
                </span>
              }

              @if (canModify(comment)) {
                <span class="tc__actions">
                  <button
                    type="button"
                    class="tc__icon-btn"
                    data-testid="testrun-comment-edit"
                    [attr.aria-label]="'testRun.comments.editAria' | translate"
                    (click)="startEdit(comment)"
                  >
                    <mat-icon aria-hidden="true">edit</mat-icon>
                  </button>
                  <button
                    type="button"
                    class="tc__icon-btn"
                    data-testid="testrun-comment-delete"
                    [attr.aria-label]="'testRun.comments.deleteAria' | translate"
                    (click)="remove(comment)"
                  >
                    <mat-icon aria-hidden="true">delete</mat-icon>
                  </button>
                </span>
              }
            </div>

            @if (editingId() === comment.id) {
              <div class="tc__edit">
                <textarea
                  class="tc__textarea"
                  rows="2"
                  data-testid="testrun-comment-edit-textarea"
                  [maxlength]="textMaxLength"
                  [attr.aria-label]="'testRun.comments.editAria' | translate"
                  [ngModel]="editDraft()"
                  (ngModelChange)="editDraft.set($event)"
                ></textarea>
                <div class="tc__edit-actions">
                  <button
                    type="button"
                    class="tc__btn"
                    data-testid="testrun-comment-edit-cancel"
                    (click)="cancelEdit()"
                  >
                    {{ 'testRun.comments.cancel' | translate }}
                  </button>
                  <button
                    type="button"
                    class="tc__btn tc__btn--primary"
                    data-testid="testrun-comment-edit-save"
                    (click)="saveEdit(comment)"
                  >
                    {{ 'testRun.comments.save' | translate }}
                  </button>
                </div>
              </div>
            } @else if (isHandover(comment)) {
              @let recipient = handoverRecipient(comment) ?? ('testRun.comments.handoverUnknownRecipient' | translate);
              @let note = comment.text ? ': ' + comment.text : '';
              <p class="tc__text" data-testid="testrun-comment-handover-text">
                {{ 'testRun.comments.handoverTo' | translate: { name: recipient } }}{{ note }}
              </p>
            } @else {
              <p class="tc__text">{{ comment.text }}</p>
            }

            @if (comment.attachments.length > 0) {
              <ul class="tc__chips">
                @for (attachment of comment.attachments; track attachment.id) {
                  <li class="tc__chip">
                    <button
                      #chip
                      type="button"
                      class="tc__chip-open"
                      data-testid="testrun-comment-attachment-chip"
                      [attr.aria-label]="
                        'testRun.comments.openImageAria' | translate: { name: attachment.originalFilename }
                      "
                      (click)="openAttachment(attachment, chip)"
                    >
                      <mat-icon class="tc__chip-icon" aria-hidden="true">image</mat-icon>
                      {{ attachment.originalFilename }}
                    </button>
                    @if (canModify(comment)) {
                      <button
                        type="button"
                        class="tc__chip-remove"
                        data-testid="testrun-comment-attachment-remove"
                        [attr.aria-label]="
                          'testRun.comments.removeImageAria' | translate: { name: attachment.originalFilename }
                        "
                        (click)="removeAttachment(attachment)"
                      >
                        <mat-icon aria-hidden="true">close</mat-icon>
                      </button>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        </li>
      }
    </ol>
  }

  @if (canComment()) {
    <div class="tc__form">
      <textarea
        class="tc__textarea"
        rows="2"
        data-testid="testrun-comment-textarea"
        [maxlength]="textMaxLength"
        [attr.aria-label]="'testRun.comments.newAria' | translate"
        [placeholder]="'testRun.comments.placeholder' | translate"
        [ngModel]="draft()"
        (ngModelChange)="draft.set($event)"
      ></textarea>

      @if (pendingFiles().length > 0) {
        <ul class="tc__chips tc__chips--pending">
          @for (file of pendingFiles(); track file.name + $index) {
            <li class="tc__chip">
              <span class="tc__chip-open">
                <mat-icon class="tc__chip-icon" aria-hidden="true">image</mat-icon>
                {{ file.name }}
              </span>
              <button
                type="button"
                class="tc__chip-remove"
                data-testid="testrun-comment-pending-remove"
                [attr.aria-label]="'testRun.comments.removeImageAria' | translate: { name: file.name }"
                (click)="removePendingFile($index)"
              >
                <mat-icon aria-hidden="true">close</mat-icon>
              </button>
            </li>
          }
        </ul>
      }

      <div class="tc__form-actions">
        <input
          #filePicker
          type="file"
          class="tc__file-input"
          multiple
          [accept]="imageAccept"
          (change)="onFilesPicked(filePicker)"
        />
        <button
          type="button"
          class="tc__btn"
          data-testid="testrun-comment-attach-btn"
          [attr.aria-label]="'testRun.comments.attachAria' | translate"
          (click)="filePicker.click()"
        >
          <mat-icon class="tc__btn-icon" aria-hidden="true">image</mat-icon>
          {{ 'testRun.comments.attach' | translate }}
        </button>

        <button
          type="button"
          class="tc__btn tc__btn--primary"
          data-testid="testrun-comment-submit"
          [disabled]="!canSubmit()"
          (click)="submit()"
        >
          {{ 'testRun.comments.submit' | translate }}
        </button>
      </div>
    </div>
  }

  @if (lightbox.url(); as url) {
    <app-image-lightbox [url]="url" [alt]="'testRun.comments.imageAlt' | translate" (closed)="lightbox.close()" />
  }
</section>
